// =============================================================================
// io/sync/savePlan.ts — the pre-market plan write.
// -----------------------------------------------------------------------------
// The second journal write, and the one D62 said had to wait. It writes NINE
// columns: the composite key, the six flattened plan columns, `disqualifiers`,
// and the `pre_market_plan` blob.
//
// ── 🚨 THE BLOB IS MERGED, NOT REBUILT ──
//
// `planToBlob(plan, raw)` starts from the row's OWN stored blob and overwrites
// only the keys this client models. That is the whole safety argument, and it is
// not theoretical: three round-trip losses shipped in this module before it
// existed, and a fourth was found while writing the serialiser. `rawPlans` on the
// read result is where `raw` comes from.
//
// ⚠ Passing `{}` as `raw` turns the merge back into a rebuild and silently drops
// every unmodelled key. The caller must hand over the blob for THIS date.
//
// ── 🚨 `disqualifiers` MOVES BETWEEN LAYERS, AND BOTH ENDS ARE EASY TO GET WRONG ──
//
// The old client reads it from INSIDE the plan (`p.disqualifiers`), writes it to
// its OWN COLUMN, and strips it from the blob. This client exposes it at
// `entry.disqualifiers` — top level, not on the plan. So the write takes the top
// level value, puts it in the column, and `planToBlob` keeps it out of the blob.
// A copy left in the blob would OVERRIDE the column in the old client, whose
// loader spreads the blob last.
//
// ── WHAT IT DOES NOT WRITE ──
//
// `booleans`, the eod_* columns, `readiness_items`, `readiness_completed_at`,
// `session_contract`, `mid_session_notes`, `recordings`, `extras`, `created_at`.
// Same rule as the recap write: never send a column this module does not own. The
// contract and readiness gate in particular carry state machines that a plan form
// has no business touching.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import { planToBlob } from '../../domain/journal';
import type { PreMarketPlan } from '../../domain/journal';
import { reportFailureAs } from '../diagnostics/errorLog';

/** Every column this module writes. Fixed, and asserted by a test. */
export const PLAN_COLUMNS = [
  'user_id', 'date',
  'market_regime', 'gamma_regime', 'overall_bias',
  'market_regime_analysis', 'gamma_regime_analysis', 'bias_summary',
  'disqualifiers',
  'pre_market_plan',
] as const;

export interface SavePlanResult {
  updatedAt: string | null;
  error: string | null;
  heldStale: boolean;
}

export interface SavePlanArgs {
  sb: SupabaseClient;
  userId: string;
  date: string;
  plan: PreMarketPlan;
  /** Top-level on the entry, NOT on the plan. See the header. */
  disqualifiers: string;
  /** The row's stored blob. `{}` only for a date that has no row yet. */
  rawPlan: Readonly<Record<string, unknown>>;
  baselineUpdatedAt: string | null;
}

export function planRow(
  date: string,
  plan: PreMarketPlan,
  disqualifiers: string,
  rawPlan: Readonly<Record<string, unknown>>,
  userId: string,
): Record<string, unknown> {
  return {
    user_id: userId,
    date,
    // The six flattened columns. The old client keeps these OUT of the blob, and
    // `planToBlob` strips them for the same reason.
    market_regime: plan.marketRegime,
    gamma_regime: plan.gammaRegime,
    overall_bias: plan.overallBias,
    market_regime_analysis: plan.marketRegimeAnalysis,
    gamma_regime_analysis: plan.gammaRegimeAnalysis,
    bias_summary: plan.biasSummary,
    disqualifiers,
    pre_market_plan: planToBlob(plan, rawPlan),
  };
}

export async function savePlan(args: SavePlanArgs): Promise<SavePlanResult> {
  const { sb, userId, date, plan, disqualifiers, rawPlan, baselineUpdatedAt } = args;
  const row = planRow(date, plan, disqualifiers, rawPlan, userId);

  // ⚠ FAIL-OPEN stale check, identical to the recap write. A guard that blocked
  // saves it could not verify would lose real work on any transient blip.
  if (baselineUpdatedAt) {
    const { data, error } = await sb
      .from('journal_entries')
      .select('date, updated_at')
      .eq('user_id', userId)
      .eq('date', date);

    if (!error && Array.isArray(data) && data.length === 1) {
      const cloud = (data[0] as unknown as Record<string, unknown>)['updated_at'];
      if (typeof cloud === 'string'
        && new Date(cloud).getTime() > new Date(baselineUpdatedAt).getTime()) {
        return {
          updatedAt: null,
          heldStale: true,
          error:
            `This day was changed somewhere else after you loaded it — probably the ` +
            `other client or another tab. Nothing was saved, so neither version is ` +
            `lost. Reload to see the newer copy, then re-apply your edit.`,
        };
      }
    }
  }

  const { data, error } = await sb
    .from('journal_entries')
    .upsert(row, { onConflict: 'user_id,date' })
    .select('date, updated_at');

  if (error) {
    return {
      updatedAt: null,
      heldStale: false,
      error: reportFailureAs(
        'save pre-market plan', error, `Save failed: ${error.message}`,
      ),
    };
  }

  // With `resolution=merge-duplicates` every payload object either inserts or
  // updates, so exactly one row comes back. Zero is a real failure here.
  if (!Array.isArray(data) || data.length !== 1) {
    return {
      updatedAt: null,
      heldStale: false,
      error:
        `The save returned ${Array.isArray(data) ? data.length : 'no'} rows instead of one, ` +
        `so it cannot be confirmed. Reload before editing again.`,
    };
  }

  const ts = (data[0] as unknown as Record<string, unknown>)['updated_at'];
  return { updatedAt: typeof ts === 'string' ? ts : null, heldStale: false, error: null };
}
